import { ScenarioDef, TilePosition, PathDef, WaveDef, TreeSlotDef, RegionType, BiomeType } from './LevelLoader';
import { TowerType } from '../entities/Tower';
import { MonsterType } from '../entities/Monster';

// ============================================
// Custom Level Manager
// Handles saving/loading user-created maps
// ============================================

const STORAGE_KEY = 'custom_levels';
const CURRENT_VERSION = 1;

// Custom level extends ScenarioDef with metadata
export interface CustomLevel extends ScenarioDef {
  isCustom: true;
  version: number;
  createdAt: number;
  updatedAt: number;
  hasManualTrees: boolean;  // If true, skip dynamic tree generation
  biome?: BiomeType;  // Visual biome: forest (default), bog, ruins/highlands, or volcano
  preResearchedTowers?: TowerType[];  // Towers that start already researched (subset of allowedTowers)
}

// Saved data format in localStorage
interface CustomLevelStorage {
  version: number;
  levels: Record<string, CustomLevel>;
}

// Default wave templates based on difficulty
const WAVE_TEMPLATES: Record<number, WaveDef[]> = {
  1: [
    { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 8, interval: 1.5, pathId: 'path-1' }] },
    { waveNumber: 2, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.3, pathId: 'path-1' }] },
    { waveNumber: 3, spawns: [{ monsterType: 'runner', count: 6, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 4, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 5, spawns: [{ monsterType: 'runner', count: 8, interval: 0.8, pathId: 'path-1' }] },
  ],
  2: [
    { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 10, interval: 1.2, pathId: 'path-1' }] },
    { waveNumber: 2, spawns: [{ monsterType: 'runner', count: 8, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 3, spawns: [{ monsterType: 'brute', count: 2, interval: 2.0, pathId: 'path-1' }] },
    { waveNumber: 4, spawns: [{ monsterType: 'bat', count: 6, interval: 1.2, pathId: 'path-1' }] },
    { waveNumber: 5, spawns: [{ monsterType: 'crawler', count: 15, interval: 0.8, pathId: 'path-1' }] },
    { waveNumber: 6, spawns: [{ monsterType: 'shielded', count: 4, interval: 1.5, pathId: 'path-1' }] },
  ],
  3: [
    { waveNumber: 1, spawns: [{ monsterType: 'crawler', count: 12, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 2, spawns: [{ monsterType: 'runner', count: 10, interval: 0.8, pathId: 'path-1' }] },
    { waveNumber: 3, spawns: [{ monsterType: 'shielded', count: 5, interval: 1.5, pathId: 'path-1' }] },
    { waveNumber: 4, spawns: [{ monsterType: 'flyer', count: 8, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 5, spawns: [{ monsterType: 'brute', count: 3, interval: 2.0, pathId: 'path-1' }] },
    { waveNumber: 6, spawns: [{ monsterType: 'swarm', count: 15, interval: 0.4, pathId: 'path-1' }] },
    { waveNumber: 7, spawns: [{ monsterType: 'crawler', count: 20, interval: 0.6, pathId: 'path-1' }] },
  ],
  4: [
    { waveNumber: 1, spawns: [{ monsterType: 'shielded', count: 8, interval: 1.2, pathId: 'path-1' }] },
    { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 10, interval: 0.9, pathId: 'path-1' }] },
    { waveNumber: 3, spawns: [{ monsterType: 'brute', count: 4, interval: 1.8, pathId: 'path-1' }] },
    { waveNumber: 4, spawns: [{ monsterType: 'swarm', count: 20, interval: 0.3, pathId: 'path-1' }] },
    { waveNumber: 5, spawns: [{ monsterType: 'juggernaut', count: 1, interval: 1, pathId: 'path-1' }] },
    { waveNumber: 6, spawns: [{ monsterType: 'crawler', count: 25, interval: 0.5, pathId: 'path-1' }] },
    { waveNumber: 7, spawns: [{ monsterType: 'phased', count: 6, interval: 1.2, pathId: 'path-1' }] },
    { waveNumber: 8, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 3.0, pathId: 'path-1' }] },
  ],
  5: [
    { waveNumber: 1, spawns: [{ monsterType: 'brute', count: 5, interval: 1.5, pathId: 'path-1' }] },
    { waveNumber: 2, spawns: [{ monsterType: 'flyer', count: 12, interval: 0.8, pathId: 'path-1' }] },
    { waveNumber: 3, spawns: [{ monsterType: 'shielded', count: 10, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 4, spawns: [{ monsterType: 'swarm', count: 30, interval: 0.25, pathId: 'path-1' }] },
    { waveNumber: 5, spawns: [{ monsterType: 'juggernaut', count: 2, interval: 3.0, pathId: 'path-1' }] },
    { waveNumber: 6, spawns: [{ monsterType: 'phased', count: 8, interval: 1.0, pathId: 'path-1' }] },
    { waveNumber: 7, spawns: [{ monsterType: 'crawler', count: 30, interval: 0.4, pathId: 'path-1' }] },
    { waveNumber: 8, spawns: [{ monsterType: 'brute', count: 6, interval: 1.5, pathId: 'path-1' }] },
    { waveNumber: 9, spawns: [{ monsterType: 'juggernaut', count: 3, interval: 2.5, pathId: 'path-1' }] },
    { waveNumber: 10, spawns: [{ monsterType: 'phased', count: 10, interval: 0.8, pathId: 'path-1' }] },
  ],
};

class CustomLevelManagerClass {
  private storage: CustomLevelStorage;
  /** Level passed from map editor when testing without saving (e.g. IMPORT_xxx or unsaved custom) */
  private pendingLevelForTest: CustomLevel | null = null;

  constructor() {
    this.storage = this.loadStorage();
  }

  /**
   * Set a level to use for the next loadScenario call (e.g. when testing from editor without saving).
   * Cleared after use or when starting a different scenario.
   */
  setPendingLevelForTest(level: CustomLevel | null): void {
    this.pendingLevelForTest = level;
  }

  /**
   * Get and clear the pending level if the id matches. Used by LevelLoader.loadScenario.
   */
  getAndClearPendingLevelForTest(scenarioId: string): CustomLevel | null {
    const level = this.pendingLevelForTest;
    this.pendingLevelForTest = null;
    if (level && level.id === scenarioId) return level;
    return null;
  }

  private loadStorage(): CustomLevelStorage {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data) as CustomLevelStorage;
        if (parsed.version === CURRENT_VERSION) {
          return parsed;
        }
        // Future: handle migrations
      }
    } catch (e) {
      console.error('[CustomLevelManager] Failed to load storage:', e);
    }
    return { version: CURRENT_VERSION, levels: {} };
  }

  private saveStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.storage));
      import('./SaveSyncService').then(({ SaveSyncService }) => {
        SaveSyncService.schedulePush();
      }).catch(() => {});
    } catch (e) {
      console.error('[CustomLevelManager] Failed to save storage:', e);
    }
  }

  /**
   * Create a new custom level with default values
   */
  createNewLevel(name: string): CustomLevel {
    const id = `CUSTOM_${Date.now()}`;
    const now = Date.now();

    const level: CustomLevel = {
      // Custom metadata
      isCustom: true,
      version: CURRENT_VERSION,
      createdAt: now,
      updatedAt: now,
      hasManualTrees: false,
      biome: 'forest',

      // Scenario definition
      id,
      name,
      description: 'Custom map',
      difficulty: 2,
      region: 'forest',
      regionNumber: 1,
      mapSize: { w: 48, h: 27 },  // 16:9 aspect ratio with double granularity
      tileSize: 45,  // Half of 90 for double granularity
      base: { x: -1, y: -1 },  // Not placed yet (invalid position)
      playerSpawns: [],  // Start blank - user must place spawns
      treeSlots: [],
      paths: [],
      waves: [],
      startingCoins: 100,
      startingGems: 25,
      startingVillagers: 10,
      allowedTowers: ['arrow', 'cannon', 'antiair', 'tesla', 'fire', 'ice', 'mortar', 'laser'],
      preResearchedTowers: ['arrow', 'cannon', 'antiair'],  // Tier 1 towers start unlocked
      unlocks: [],
    };

    return level;
  }

  /**
   * Generate waves for a custom level based on its paths and difficulty
   */
  generateWaves(level: CustomLevel): WaveDef[] {
    const template = WAVE_TEMPLATES[level.difficulty] || WAVE_TEMPLATES[2];
    const pathIds = level.paths.map(p => p.id);
    
    if (pathIds.length === 0) {
      console.warn('[CustomLevelManager] No paths defined, using default path-1');
      return template;
    }

    // Distribute waves across paths
    const waves: WaveDef[] = template.map((wave, index) => {
      const newSpawns = wave.spawns.map(spawn => {
        // Distribute spawns across paths for variety
        const pathIndex = index % pathIds.length;
        return {
          ...spawn,
          pathId: pathIds[pathIndex],
        };
      });

      return {
        ...wave,
        spawns: newSpawns,
      };
    });

    // For multi-path maps, add some waves that use multiple paths simultaneously
    if (pathIds.length > 1) {
      const extraWaves: WaveDef[] = [];
      const baseWaveCount = waves.length;

      // Add 2-3 extra waves that split across paths
      for (let i = 0; i < Math.min(3, pathIds.length); i++) {
        const waveNum = baseWaveCount + i + 1;
        const spawns = pathIds.map((pathId, pathIndex) => ({
          monsterType: (['crawler', 'runner', 'shielded'] as MonsterType[])[pathIndex % 3],
          count: 6 + level.difficulty * 2,
          interval: 1.0 - level.difficulty * 0.1,
          pathId,
          delay: pathIndex * 2,  // Stagger spawns
        }));

        extraWaves.push({
          waveNumber: waveNum,
          spawns,
        });
      }

      waves.push(...extraWaves);
    }

    return waves;
  }

  /**
   * Save a custom level
   */
  saveLevel(level: CustomLevel): void {
    level.updatedAt = Date.now();
    
    // Auto-generate waves if needed
    if (level.waves.length === 0 && level.paths.length > 0) {
      level.waves = this.generateWaves(level);
    }

    // Mark as having manual trees if any trees exist
    if (level.treeSlots.length > 0) {
      level.hasManualTrees = true;
    }

    this.storage.levels[level.id] = level;
    this.saveStorage();
    console.log(`[CustomLevelManager] Saved level: ${level.name} (${level.id})`);
  }

  /**
   * Delete a custom level
   */
  deleteLevel(id: string): boolean {
    if (this.storage.levels[id]) {
      delete this.storage.levels[id];
      this.saveStorage();
      console.log(`[CustomLevelManager] Deleted level: ${id}`);
      return true;
    }
    return false;
  }

  /**
   * Get a custom level by ID
   */
  getLevel(id: string): CustomLevel | null {
    return this.storage.levels[id] || null;
  }

  /**
   * Get all custom levels
   */
  getAllLevels(): CustomLevel[] {
    return Object.values(this.storage.levels).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  /**
   * Replace all custom levels (e.g. when restoring from a full save import).
   * Validates each level and skips invalid entries.
   */
  replaceAllLevels(levels: CustomLevel[]): void {
    const record: Record<string, CustomLevel> = {};
    for (const level of levels) {
      if (!level || !level.name || !level.base || !level.mapSize) continue;
      const normalized: CustomLevel = {
        ...level,
        isCustom: true,
        version: CURRENT_VERSION,
        updatedAt: level.updatedAt ?? Date.now(),
        createdAt: level.createdAt ?? Date.now(),
      };
      record[normalized.id] = normalized;
    }
    this.storage.levels = record;
    this.saveStorage();
    console.log(`[CustomLevelManager] Replaced all levels (${Object.keys(record).length} maps)`);
  }

  /**
   * Check if a level ID is a custom level
   */
  isCustomLevel(id: string): boolean {
    return id.startsWith('CUSTOM_') || !!this.storage.levels[id];
  }

  /**
   * Duplicate an existing custom level
   */
  duplicateLevel(id: string, newName: string): CustomLevel | null {
    const original = this.getLevel(id);
    if (!original) return null;

    const newLevel = this.createNewLevel(newName);
    
    // Copy all data except metadata
    newLevel.description = original.description;
    newLevel.difficulty = original.difficulty;
    newLevel.region = original.region;
    newLevel.mapSize = { ...original.mapSize };
    newLevel.tileSize = original.tileSize;
    newLevel.base = { ...original.base };
    newLevel.playerSpawns = original.playerSpawns.map(p => ({ ...p }));
    newLevel.treeSlots = original.treeSlots.map(t => ({ ...t }));
    newLevel.paths = original.paths.map(p => ({
      ...p,
      points: p.points?.map(pt => ({ ...pt })),
      routes: p.routes ? Object.fromEntries(
        Object.entries(p.routes).map(([k, v]) => [k, v.map(pt => ({ ...pt }))])
      ) : undefined,
      distribution: p.distribution ? { ...p.distribution } : undefined,
    }));
    newLevel.waves = original.waves.map(w => ({
      ...w,
      spawns: w.spawns.map(s => ({ ...s })),
      boss: w.boss ? { ...w.boss } : undefined,
    }));
    newLevel.startingCoins = original.startingCoins;
    newLevel.startingGems = original.startingGems;
    newLevel.startingVillagers = original.startingVillagers;
    newLevel.allowedTowers = [...original.allowedTowers];
    newLevel.preResearchedTowers = original.preResearchedTowers ? [...original.preResearchedTowers] : undefined;
    newLevel.hasManualTrees = original.hasManualTrees;
    newLevel.biome = original.biome ?? 'forest';

    this.saveLevel(newLevel);
    return newLevel;
  }

  /**
   * Export level to JSON string
   */
  exportLevel(id: string): string | null {
    const level = this.getLevel(id);
    if (!level) return null;
    return JSON.stringify(level, null, 2);
  }

  /**
   * Import level from JSON string
   */
  importLevel(json: string): CustomLevel | null {
    try {
      const data = JSON.parse(json) as CustomLevel;
      
      // Validate required fields
      if (!data.name || !data.base || !data.mapSize) {
        console.error('[CustomLevelManager] Invalid level data');
        return null;
      }

      // Generate new ID to avoid conflicts
      const newId = `CUSTOM_${Date.now()}`;
      const now = Date.now();

      const level: CustomLevel = {
        ...data,
        id: newId,
        isCustom: true,
        version: CURRENT_VERSION,
        createdAt: now,
        updatedAt: now,
      };

      this.saveLevel(level);
      return level;
    } catch (e) {
      console.error('[CustomLevelManager] Failed to import level:', e);
      return null;
    }
  }
}

export const CustomLevelManager = new CustomLevelManagerClass();
